// src/shared/services/NetworkStatusService.ts

type Listener = (isOnline: boolean) => void;

const CHANNEL_NAME = 'network-status';
const PROBE_INTERVAL_MS = 10_000;
const SW_RESPONSE_TIMEOUT_MS = 3_000;

// Resolved lazily so that SW top-level globalThis assignments (e.g. in firebase-sw.ts)
// are visible even though ES-module imports are evaluated before that code runs.
function getHealthUrl(): string {
  return (
    (typeof process !== 'undefined' &&
      // @ts-ignore allow env replacement in client bundle
      process.env?.REACT_APP_HAS_LIVE_CONNECTION_URL) ||
    (globalThis as any).REACT_APP_HAS_LIVE_CONNECTION_URL ||
    '/healthz'
  );
}

function isWindowScope(): boolean {
  return typeof window !== 'undefined' && typeof document !== 'undefined';
}

export default class NetworkStatusService {
  private static instance: NetworkStatusService;

  private isOnline: boolean =
    isWindowScope() && 'onLine' in navigator ? navigator.onLine : true;

  private listeners: Listener[] = [];
  private bc?: BroadcastChannel;
  private nextProbeAt = 0;
  private handleOnline = () => this.updateLocal(true);
  private handleOffline = () => {
    // Show offline UI immediately so the user gets instant feedback.
    this.updateLocal(false);
    // Then probe to verify — if the browser event was stale, self-correct.
    this.probe().then((actuallyOnline) => {
      if (actuallyOnline) this.updateLocal(true);
    });
  };

  private constructor() {
    this.updateLocal = this.updateLocal.bind(this);
    this.handleBCMessage = this.handleBCMessage.bind(this);

    if (typeof BroadcastChannel !== 'undefined') {
      this.bc = new BroadcastChannel(CHANNEL_NAME);
      this.bc.addEventListener('message', this.handleBCMessage);
    }

    if (isWindowScope()) {
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('offline', this.handleOffline);

      // If navigator.onLine is already false (stale from a previous outage),
      // run an authoritative probe to self-correct before any subscriber reads it.
      if (!this.isOnline) {
        this.probe().then((actuallyOnline) => {
          this.updateLocal(actuallyOnline);
        });
      }

      // Ask SW (if present) to run an authoritative probe
      navigator.serviceWorker?.controller?.postMessage({ type: 'NET_STATUS_POLL' });
    } else {
      // Service Worker wiring (use globalThis instead of self)
      const sw = globalThis as any;

      // Re-probe when a client asks
      sw.addEventListener('message', (evt: any) => {
        if (evt?.data?.type === 'NET_STATUS_POLL') {
          evt.waitUntil(this.probeAndPublish(true));
        }
      });

      // Occasionally refresh based on traffic (no timers; SW can be killed anytime)
      sw.addEventListener('fetch', (evt: any) => {
        if (Date.now() >= this.nextProbeAt) {
          this.nextProbeAt = Date.now() + PROBE_INTERVAL_MS;
          evt.waitUntil(this.probeAndPublish());
        }
      });

      // Initial probe
      sw.addEventListener('activate', (evt: any) => {
        evt.waitUntil(this.probeAndPublish());
      });
    }
  }

  public static getInstance(): NetworkStatusService {
    if (!NetworkStatusService.instance) {
      NetworkStatusService.instance = new NetworkStatusService();
    }
    return NetworkStatusService.instance;
  }

  public addListener(callback: Listener) {
    this.listeners.push(callback);
    callback(this.isOnline); // initialize subscriber immediately
  }

  public removeListener(callback: Listener) {
    this.listeners = this.listeners.filter((l) => l !== callback);
  }

  public async getLatestStatusConnection(): Promise<boolean> {
    if (isWindowScope()) {
      const swController = navigator.serviceWorker?.controller;
      
      // If no SW or no BroadcastChannel, probe directly (no point waiting 3s)
      if (!swController || !this.bc) {
        const online = await this.probe();
        this.updateLocal(online);
        return online;
      }

      // Ask SW to probe; resolve when it broadcasts the result
      return new Promise<boolean>((resolve) => {
        let resolved = false;
        const once = (evt: MessageEvent) => {
          if (resolved) return;
          const data = (evt?.data ?? {}) as any;
          if (data.type === 'status') {
            resolved = true;
            this.bc?.removeEventListener('message', once);
            const online = !!data.isOnline;
            this.updateLocal(online);
            resolve(online);
          }
        };
        this.bc.addEventListener('message', once);
        swController.postMessage({ type: 'NET_STATUS_POLL' });

        // Fallback if SW doesn’t respond quickly
        setTimeout(async () => {
          if (resolved) return;
          resolved = true;
          this.bc?.removeEventListener('message', once);
          const online = await this.probe();
          this.updateLocal(online);
          resolve(online);
        }, SW_RESPONSE_TIMEOUT_MS);
      });
    } else {
      // In SW: probe directly and publish to clients
      await this.probeAndPublish(true);
      return this.isOnline;
    }
  }

  public getStatus() {
    return this.isOnline;
  }

  /**
   * Cleanup method to remove event listeners and close the BroadcastChannel.
   * Note: This is primarily useful for testing. In production, the service
   * typically lives for the entire app lifetime.
   */
  public destroy() {
    if (isWindowScope()) {
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('offline', this.handleOffline);
    }
    this.bc?.removeEventListener('message', this.handleBCMessage);
    this.bc?.close();
    this.bc = undefined;
    this.listeners = [];
  }

  // ---------- private ----------

  private notifyListeners() {
    for (const cb of this.listeners) cb(this.isOnline);
  }

  private updateLocal(val: boolean) {
    if (this.isOnline !== val) {
      this.isOnline = val;
      this.notifyListeners();
    }
  }

  private handleBCMessage(evt: MessageEvent) {
    const data = (evt?.data ?? {}) as any;
    if (data.type === 'status') {
      this.updateLocal(!!data.isOnline);
    }
  }

  private async probe(): Promise<boolean> {
    try {
      const resp = await fetch(getHealthUrl(), {
        method: 'HEAD',
        cache: 'no-store',
        mode: 'no-cors',
      });
      // With no-cors, opaque means “likely reachable”.
      return resp.ok || resp.type === 'opaque';
    } catch {
      return false;
    }
  }

  private async probeAndPublish(forceNotify = false) {
    const newStatus = await this.probe();
    const changed = newStatus !== this.isOnline;
    if (changed) {
      this.isOnline = newStatus;
      this.notifyListeners();
    }
    if (changed || forceNotify) {
      this.bc?.postMessage({ type: 'status', isOnline: this.isOnline, ts: Date.now() });
    }
  }
}
